import { isAxiosError } from "axios";

import {
  KNOWLEDGE_BYTES_PER_MB,
  KNOWLEDGE_FILE_TOO_LARGE_I18N_KEY,
  KNOWLEDGE_PROCESSING_ERROR_I18N_KEYS,
} from "../constants/knowledge";
import type {
  IClassifyKnowledgeUploadErrorProps,
  IGetKnowledgeProcessingErrorI18nKeyProps,
  IIsKnowledgeFileOverSizeLimitProps,
  IKnowledgeUploadErrorMessage,
  TKnowledgeProcessingErrorI18nKey,
} from "../types/knowledge";

const HTTP_PAYLOAD_TOO_LARGE = 413;
// NOTE: The shared copy for a request that never got an answer — the same
// sentence the chat and assistant uploads show (see upload-error.ts).
const UPLOAD_NETWORK_ERROR_I18N_KEY = "common:upload_error.network" as const;
// NOTE: An oversized file when the limit itself is unknown (an older backend).
const UPLOAD_TOO_LARGE_I18N_KEY = "common:upload_error.too_large" as const;

// NOTE: NSAI-7817 — the translation key for a failed file's stored reason, or
// null when there is none (an older backend, a file that failed before the
// code existed); callers then keep their current failed-state copy.
export const getKnowledgeProcessingErrorI18nKey = ({
  code,
}: IGetKnowledgeProcessingErrorI18nKeyProps): TKnowledgeProcessingErrorI18nKey | null => {
  if (!code) {
    return null;
  }
  // NOTE: Own keys only — a value outside the enum must not resolve through
  // the object prototype.
  return Object.hasOwn(KNOWLEDGE_PROCESSING_ERROR_I18N_KEYS, code)
    ? KNOWLEDGE_PROCESSING_ERROR_I18N_KEYS[code]
    : null;
};

// NOTE: NSAI-7817 — whether a picked file is over the backend's knowledge
// upload cap, so it is refused before its bytes are sent. False whenever either
// side is unknown: the server still enforces the cap, and a guessed limit would
// wrongly block uploads on a deployment that raised it.
export const isKnowledgeFileOverSizeLimit = ({
  fileSizeBytes,
  maxFileSizeMb,
}: IIsKnowledgeFileOverSizeLimitProps): boolean =>
  fileSizeBytes !== undefined &&
  maxFileSizeMb !== undefined &&
  maxFileSizeMb > 0 &&
  fileSizeBytes > maxFileSizeMb * KNOWLEDGE_BYTES_PER_MB;

// NOTE: NSAI-7817 — the readable reason for a failed knowledge upload, for the
// cases the server's own message cannot explain: a file over the cap (seen
// before upload, or answered 413 — multer's "File too large" is untranslated
// and names no limit) and a request that never got an answer. Returns null for
// everything else, so the caller keeps showing the server's message (a 409
// "already uploaded", say), exactly as before.
export const classifyKnowledgeUploadError = ({
  error,
  fileSizeBytes,
  maxFileSizeMb,
}: IClassifyKnowledgeUploadErrorProps): IKnowledgeUploadErrorMessage | null => {
  const isOverLimit = isKnowledgeFileOverSizeLimit({
    fileSizeBytes,
    maxFileSizeMb,
  });
  const isPayloadTooLarge =
    isAxiosError(error) && error.response?.status === HTTP_PAYLOAD_TOO_LARGE;

  if (isOverLimit || isPayloadTooLarge) {
    return maxFileSizeMb !== undefined && maxFileSizeMb > 0
      ? {
          i18nKey: KNOWLEDGE_FILE_TOO_LARGE_I18N_KEY,
          i18nParams: { limit: maxFileSizeMb },
        }
      : { i18nKey: UPLOAD_TOO_LARGE_I18N_KEY, i18nParams: {} };
  }
  // NOTE: No response at all — offline, a dropped connection, a proxy that
  // closed the socket. Connectivity, not a rejection.
  if (isAxiosError(error) && !error.response && error.code !== "ERR_CANCELED") {
    return { i18nKey: UPLOAD_NETWORK_ERROR_I18N_KEY, i18nParams: {} };
  }
  return null;
};
